import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { redirect } from 'redux-first-router';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';

import { ROUTE_PORTFOLIO } from '@/constants/routes';

import {
  Heading,
  Alert,
  Box,
  Button,
  Grid,
  Text,
  useTheme,
  ScrollPanel,
  Badge,
} from '@simplywallst/ui-core';

import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import TickSolid from '@simplywallst/ui-core/icons/TickSolid';

import Tooltip from '@/components/Tooltip';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { HoldingInputRow } from './HoldingInputRow';

import type { ManualPortfolioState } from '../CreateManualPortfolio';

import { useUpdateManualPortfolio } from '../../hooks/useUpdateManualPortfolio';
import type { UpdateManualHoldingInput } from '../../hooks/useUpdateManualPortfolio';
import { useManualPortfolioTracking } from '../../hooks/useManualPortfolioTracking';

import type { PortfolioToUpdate } from '../../context/ManualPortfolioContext';
import { isIncompleteManualPortfolio } from '../../utils';

import { isSWSGQLError } from '@/utilities/graphQLClient';
import type { SWSGQLError } from '@/utilities/graphQLClient';

import { StyledTable, TableHeadCell } from './styled';

interface Props {
  initialData?: PortfolioToUpdate;
  portfolio: Portfolio;
  onClose: () => void;
  setManualPortfolioState: (state: ManualPortfolioState) => void;
}

const VALUE_FAILED_VALIDATION = 'PRTFL-003';

const Error = ({ error }: { error: SWSGQLError }) => {
  return (
    <Box paddingX="x3" paddingY="x2">
      <Alert styleType="negative">
        <Text typography="small" color="white">
          {error.code === VALUE_FAILED_VALIDATION ? (
            <>
              One or more inputted values are larger than 10<sup>24</sup>
            </>
          ) : (
            'Unknown error'
          )}
        </Text>
      </Alert>
    </Box>
  );
};

export const EditHoldings = ({
  initialData,
  portfolio,
  onClose,
  setManualPortfolioState,
}: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const sortedHoldings = useMemo(
    () =>
      portfolio.holdings.sort(
        ({ company: { name: a } }, { company: { name: b } }) => (a < b ? -1 : 1)
      ),
    [portfolio.holdings]
  );

  const [draftHoldings, setDraftHoldings] = useState<
    UpdateManualHoldingInput[]
  >(() =>
    sortedHoldings.map((holding) => ({
      exchangeCode: holding.company.exchangeSymbol,
      symbol: holding.symbol,
      units: String(holding.units),
      averagePurchasePrice:
        holding.averagePurchasePrice === null
          ? ''
          : String(holding.averagePurchasePrice),
    }))
  );

  const { mutate, status, error } = useUpdateManualPortfolio();
  const debouncedMutate = useMemo(() => debounce(mutate, 1000), [mutate]);

  const mounted = useRef(false);

  const { trackManualPortfolioEvent } = useManualPortfolioTracking();

  useEffect(() => {
    if (!draftHoldings.length) return;
    if (!mounted.current) {
      mounted.current = true;
    } else {
      debouncedMutate({
        id: portfolio.id,
        holdings: draftHoldings,
      });
    }
  }, [debouncedMutate, draftHoldings, portfolio.id]);

  const handleUpdate = useCallback(
    ({
      exchangeCode,
      symbol,
      units,
      averagePurchasePrice,
    }: UpdateManualHoldingInput) => {
      setDraftHoldings((prevDraftHoldings) =>
        prevDraftHoldings.map((holding) => {
          if (
            holding.exchangeCode === exchangeCode &&
            holding.symbol === symbol
          ) {
            return {
              exchangeCode: holding.exchangeCode,
              symbol: holding.symbol,
              units,
              averagePurchasePrice,
            };
          }
          return holding;
        })
      );
    },
    []
  );

  const handleClose = useCallback(() => {
    const holdings = portfolio.holdings ?? [];
    const completedHoldings =
      holdings.length -
      isIncompleteManualPortfolio(holdings).incompleteHoldingCount;

    trackManualPortfolioEvent({
      modifier: 'done-button',
      action: 'click',
      portfolioId: portfolio.id,
      totalHoldings: holdings.length,
      totalCompletedHoldings: completedHoldings,
    });

    mutate({
      id: portfolio.id,
      holdings: draftHoldings,
      triggerPortfolioTimeSeriesRecalculation: true,
    });

    dispatch(
      redirect({
        type: ROUTE_PORTFOLIO,
        payload: { portfolioId: portfolio.id },
      })
    );

    onClose();
  }, [
    trackManualPortfolioEvent,
    portfolio,
    dispatch,
    onClose,
    mutate,
    draftHoldings,
  ]);

  const handleRemoveDraftHolding = useCallback(
    ({ symbol, exchangeCode }: { symbol: string; exchangeCode: string }) => {
      trackManualPortfolioEvent({
        modifier: 'remove-holding',
        action: 'click',
        portfolioId: portfolio?.id,
      });
      setDraftHoldings((prevDraftHoldings) => {
        return prevDraftHoldings.filter(
          (holding) =>
            holding.exchangeCode !== exchangeCode || holding.symbol !== symbol
        );
      });
    },
    [portfolio.id, trackManualPortfolioEvent]
  );

  const holdings = portfolio.holdings ?? [];

  const requestFailedValidation =
    isSWSGQLError(error) && error.code === VALUE_FAILED_VALIDATION;

  const completedHoldings =
    holdings.length -
    isIncompleteManualPortfolio(holdings).incompleteHoldingCount;

  const completedPortfolio =
    holdings.length > 0 && completedHoldings === holdings.length;

  return (
    <Grid templateRows="max-content 1fr max-content">
      <Grid
        gap="16px"
        alignItems="center"
        padding="x2"
        paddingLg="x3"
        templateColumns="max-content max-content"
      >
        <Heading as="h3">Add holdings details</Heading>
        <Badge tone={completedPortfolio ? 'good' : 'default'}>
          <Grid
            templateColumns={`repeat(${
              completedPortfolio ? 2 : 1
            }, max-content)`}
            alignItems="center"
            gap="8px"
            data-cy-id="holdings-tag"
          >
            {completedPortfolio ? <TickSolid /> : null}
            {completedHoldings}/{holdings.length} holdings ready
          </Grid>
        </Badge>
      </Grid>
      <Box position="relative">
        <ScrollPanel
          overflow="auto"
          position="absolute"
          height="100%"
          width="100%"
          paddingBottomLg="x2"
          paddingXLg="x3"
        >
          <StyledTable>
            <thead data-cy-id="edit-holdings-table-head">
              <tr>
                <TableHeadCell />
                <TableHeadCell>
                  <Grid
                    templateColumns="max-content 24px"
                    alignItems="center"
                    gap="4px"
                  >
                    Shares
                    <Tooltip
                      title={
                        <Grid maxWidth="250px" paddingY="x1" paddingX="x1_5">
                          <Text typography="tiny" color="spaceGray">
                            Enter the total amount of shares currently owned for
                            each stock.
                          </Text>
                        </Grid>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <Grid cursor="help">
                        <InformationSolid fill={theme.color.whiteRGBA(0.5)} />
                      </Grid>
                    </Tooltip>
                  </Grid>
                </TableHeadCell>
                <TableHeadCell>
                  <Grid
                    templateColumns="max-content 24px"
                    alignItems="center"
                    gap="4px"
                  >
                    Avg Price
                    <Tooltip
                      title={
                        <Grid maxWidth="250px" paddingY="x1" paddingX="x1_5">
                          <Text typography="tiny" color="spaceGray">
                            Input the average purchase price per stock, also
                            known as &apos;cost basis&apos; (e.g. buying 5
                            stocks at $100 and 5 at $200 equals an average price
                            of $150).
                          </Text>
                        </Grid>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <Grid cursor="help">
                        <InformationSolid fill={theme.color.whiteRGBA(0.5)} />
                      </Grid>
                    </Tooltip>
                  </Grid>
                </TableHeadCell>
              </tr>
            </thead>
            <tbody data-cy-id="edit-holdings-table-body">
              {sortedHoldings.map((holding) => {
                const draftHolding = draftHoldings.find(
                  (draftHolding) =>
                    draftHolding.exchangeCode ===
                      holding.company.exchangeSymbol &&
                    draftHolding.symbol === holding.symbol
                );
                return draftHolding ? (
                  <HoldingInputRow
                    key={holding.id}
                    holding={holding}
                    draftHolding={draftHolding}
                    onInputSave={handleUpdate}
                    initialData={initialData}
                    onRemoveDraftHolding={handleRemoveDraftHolding}
                  />
                ) : null;
              })}
            </tbody>
          </StyledTable>
        </ScrollPanel>
      </Box>
      <Grid templateRows="max-content max-content">
        {isSWSGQLError(error) && <Error error={error} />}
        <Grid
          as="footer"
          templateColumns="auto auto"
          justifyContent="space-between"
          alignItems="center"
          boxShadowTop="strongBorder"
          padding="x2"
          paddingYMd="x2"
          paddingXMd="x3"
        >
          <Button
            styleType="loudLarge"
            alignIcon="left"
            disabled={status === 'pending'}
            onClick={() => {
              trackManualPortfolioEvent({
                modifier: 'add-new-holding',
                action: 'click',
                portfolioId: portfolio.id,
              });
              setManualPortfolioState('add');
            }}
          >
            Holding
            <PlusSolid />
          </Button>
          <Grid
            templateColumns="max-content max-content"
            alignItems="center"
            gap={`${theme.x2spacePx}px`}
          >
            <div>
              {status === 'pending' && (
                <Text typography="tiny" color="mediumGray">
                  Saving...
                </Text>
              )}
              {status === 'success' && (
                <Text typography="tiny" color="mediumGray">
                  Saved
                </Text>
              )}
            </div>
            <Button
              styleType="louderLarge"
              disabled={status === 'pending' || requestFailedValidation}
              onClick={handleClose}
            >
              Done
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
